import React, { useContext, memo, Fragment } from 'react';
import { useHistory } from 'react-router-dom';
import { Table as AntdTable, Row, Typography, Button, Modal } from 'antd';
import {
  EditOutlined,
  DeleteOutlined,
  ExclamationCircleOutlined,
  MessageOutlined,
  MessageFilled,
} from '@ant-design/icons';
import { CheckboxChangeEvent } from 'antd/lib/checkbox';
import { TableCheckbox } from './index';
import UserContext from '../../../contexts/user.context';
import { SortOrder } from '../../../models/sortOrder';
import './Table.css';

const { Text, Link } = Typography;

interface IProps {
  tableData: any[];
  total: number;
  pageSize: number;
  current: number;
  sortOrder: SortOrder;
  loading: boolean;
  selectedRow: string;
  onTableChange: (pagination: any, filters: any, sorter: any) => void;
  onDelete: (id: string) => void;
  onCommentClick: (id: string) => void;
  onTableCheckboxChange: (e: CheckboxChangeEvent, eventId: any) => void;
  onTableFollowCheckboxChange: (e: CheckboxChangeEvent, eventId: any) => void;
}

const Table: React.FC<IProps> = (props: IProps) => {
  const { isAdmin } = useContext(UserContext);
  const {
    tableData,
    total,
    pageSize,
    current,
    sortOrder,
    loading,
    selectedRow,
    onTableChange,
    onDelete,
    onCommentClick,
    onTableCheckboxChange,
    onTableFollowCheckboxChange,
  } = props;

  const history = useHistory();

  const readMoreModal = (description: string) =>
    Modal.info({
      title: 'Event Description',
      content: description,
      okText: 'Close',
      centered: true,
    });

  const deleteModal = (id: string) =>
    Modal.confirm({
      title: 'Are you sure you want to delete this event?',
      icon: <ExclamationCircleOutlined />,
      okText: 'Yes',
      async onOk() {
        await onDelete(id);
      },
      cancelText: 'No',
      centered: true,
    });

  const onEdit = (id: string) => {
    history.push(`/edit/${id}`);
  };

  const generateLocationString = (
    location: string,
    stateAbbreviation: string,
  ): string => {
    if (location === null && stateAbbreviation === null) return 'TBD';
    if (location !== null && stateAbbreviation === null) return location;
    return `${location} (${stateAbbreviation})`;
  };

  const columns = [
    {
      title: 'Actions',
      key: 'action',
      width: '130px',
      fixed: true,
      render: (record: any) => (
        <>
          {(record.reviewed === '1' || isAdmin) && (
            <Row>
              <TableCheckbox
                type="reviewed"
                id={record.id}
                defaultChecked={record.reviewed === '1'}
                onChange={onTableCheckboxChange}
                isAdmin={isAdmin}
              />
            </Row>
          )}
          {(record.flagged === '1' || isAdmin) && (
            <Row>
              <TableCheckbox
                type="flagged"
                id={record.id}
                defaultChecked={record.flagged === '1'}
                onChange={onTableCheckboxChange}
                isAdmin={isAdmin}
              />
            </Row>
          )}
          <Row>
            <TableCheckbox
              type="follow"
              id={record.id}
              defaultChecked={record.following}
              onChange={onTableFollowCheckboxChange}
              isAdmin={isAdmin}
            />
          </Row>
          <Row>
            <Button
              size="large"
              type="primary"
              className="Table-button"
              icon={<EditOutlined />}
              onClick={() => onEdit(record.id)}
            >
              Edit
            </Button>
            {isAdmin && (
              <Button
                size="large"
                className="Table-button"
                icon={<DeleteOutlined />}
                onClick={() => deleteModal(record.id)}
              >
                Delete
              </Button>
            )}
            <Button
              className="Table-comments-button"
              icon={
                record.comments > 0 ? <MessageFilled /> : <MessageOutlined />
              }
              onClick={() => onCommentClick(record.id)}
            >
              Comments
            </Button>
          </Row>
        </>
      ),
    },
    {
      title: 'Update History',
      key: 'updateHistory',
      width: '150px',
      render: (record: any) => (
        <>
          <Row>
            <Text strong>Created:</Text>
          </Row>
          <Row>
            <Text>{record.createdOn}</Text>
          </Row>
          <Text>{record.createdBy}</Text>
          <Row>
            <Text strong>Latest Update:</Text>
          </Row>
          <Row>
            <Text>{record.lastUpdatedOn}</Text>
          </Row>
          <Row />
          <Text>{record.lastUpdatedBy}</Text>
        </>
      ),
    },
    {
      title: 'Event Information',
      key: 'event information',
      width: '150px',
      sorter: true,
      sortOrder,
      render: (record: any) => (
        <>
          <Row>
            <Text strong>Date:</Text>
          </Row>
          <Row>
            <Text>{record.occurredOn}</Text>
          </Row>
          <Row>
            <Text strong>Status:</Text>
          </Row>
          <Row>
            <Text>{record.status}</Text>
          </Row>
          <Row>
            <Text strong>Location:</Text>
          </Row>
          <Row>
            <Text>
              {generateLocationString(
                record.location,
                record.stateAbbreviation,
              )}
            </Text>
          </Row>
        </>
      ),
    },
    {
      title: 'Event Description',
      key: 'eventDescription',
      width: '300px',
      render: (record: any) =>
        record.description.length > 300 ? (
          <>
            <Row>
              {record.description.substring(0, 200)}...
              <Link onClick={() => readMoreModal(record.description)}>
                Read More
              </Link>
            </Row>
          </>
        ) : (
          <Row>{record.description}</Row>
        ),
    },
    {
      title: 'Format',
      key: 'format',
      width: '150px',
      dataIndex: 'format',
    },
    {
      title: 'Audience(s)',
      key: 'audience(s)',
      width: '200px',
      render: (record: any) => (
        <ul className="Table-list">
          {record.audience.map((a: any) => (
            <li key={`${record.id}:audience:${a.type}`}>
              {a.userDefined === '1' ? `Other: ${a.type}` : a.type}
            </li>
          ))}
        </ul>
      ),
    },
    {
      title: 'Participating Agencies',
      key: 'participatingAgencies',
      width: '200px',
      render: (record: any) => (
        <ul className="Table-list">
          {record.agencies.map((a: any) => (
            <li key={`${record.id}:agency:${a.agency}`}>
              {a.userDefined === '1' ? `Other: ${a.agency}` : a.agency}
            </li>
          ))}
        </ul>
      ),
    },
    {
      title: 'Attendees',
      key: 'attendees',
      width: '350px',
      render: (record: any) => (
        <>
          <Row>
            <Text strong>HHS Principal Attending/Speaking:</Text>
          </Row>
          <ul className="Table-list">
            {record.principal.length === 0 ? (
              <li>N/A</li>
            ) : (
              record.principal.map((st: any) => (
                <li key={`${record.id}:principal:${st}`}>{st}</li>
              ))
            )}
          </ul>
          <Row>
            <Text strong>Additional HHS Staff Attending/Speaking:</Text>
          </Row>
          <ul className="Table-list">
            {record.agencyAttendees.length === 0 ? (
              <li>N/A</li>
            ) : (
              record.agencyAttendees.map((st: any) => (
                <li key={`${record.id}:agency:${st}`}>{st}</li>
              ))
            )}
          </ul>
          <Row>
            <Text strong>Additional Attendees: </Text>
          </Row>
          <ul className="Table-list">
            <li key={`${record.id}:additional`}>
              {record.additionalAttendees === null
                ? 'N/A'
                : record.additionalAttendees}
            </li>
          </ul>
        </>
      ),
    },
    {
      title: 'Members of Congress',
      key: 'membersOfCongress',
      width: '200px',
      render: (record: any) => (
        <ul className="Table-list">
          {record.congressMembers.map((cm: any) => (
            <li key={`${record.id}:congress:${cm}`}>
              {cm === null ? 'N/A' : cm}
            </li>
          ))}
        </ul>
      ),
    },
    {
      title: 'EHE Jurisdiction',
      key: 'eheJurisdiction',
      width: '200px',
      render: (record: any) => (
        <>
          <Row>{record.jurisdictionVisit === '1' ? 'Yes' : 'No'}</Row>
          {record.jurisdictionsByState.map(
            (st: any) =>
              st.name !== null && (
                <Fragment key={`${record.id}:state:${st.abbreviation}`}>
                  <Row>
                    {st.name} ({st.abbreviation})
                  </Row>
                  <ul className="Table-list">
                    {st.jurisdictions.map((jd: any) => (
                      <li key={`${record.id}:jurisdiction:${jd}`}>{jd}</li>
                    ))}
                  </ul>
                </Fragment>
              ),
          )}
        </>
      ),
    },
    {
      title: 'Other Localities Reached',
      key: 'otherLocalitiesReached',
      width: '200px',
      dataIndex: 'otherLocalitiesReached',
    },
    {
      title: 'RSP',
      key: 'rsp',
      render: (record: any) =>
        record.readySetPrep === '1' ? <Row>Yes</Row> : <Row>No</Row>,
    },
  ];

  return (
    <AntdTable
      rowKey={(record) => record.id}
      columns={columns}
      dataSource={tableData}
      scroll={{
        x: 2300,
        y: 'calc(100vh - 450px)',
      }}
      pagination={{
        position: ['bottomLeft'],
        total,
        pageSize,
        current,
      }}
      loading={loading}
      bordered
      onChange={onTableChange}
      className="Table"
      rowClassName={(record) => {
        if (record.id === selectedRow) return 'Table-selected-row';
        if (record.recentEvent && isAdmin) return 'Table-recent-row';
        return '';
      }}
    />
  );
};

export default memo(Table);
